import { useParams } from "react-router-dom";
import { ESRFieldProps } from "../../types/data";
import {
  Button,
  TextField,
  IconButton,
  Chip,
  Box,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useRemoteField } from "../../hooks/useRemoteField";
import { useEffect, useState } from "react";

export const ESRMultiLineTextField = (props: ESRFieldProps) => {
  const { question_id, required, locked, inline, hint_value } = props;
  const [value, setValue] = useRemoteField(question_id);
  const [textLists, setTextList] = useState<string[]>([])

  
	useEffect(() => {
			if (!value || value.trim() === "") {
				setTextList([]);
				return;
			}

			try {
				const parsed = JSON.parse(value);

				const ans = Array.isArray(parsed)
				? parsed
				: [parsed];

				setTextList(ans);
			} catch {
				// fallback: treat as plain string (or split if needed)
				const cleaned = value.trim();

				let ans;
				if (cleaned.includes(",")) {
				ans = cleaned.split(",").map(v => v.trim()).filter(Boolean);
				} else if (cleaned.includes(" ")) {
				ans = cleaned.split(/\s+/).filter(Boolean);
				} else {
				ans = [cleaned];
				}

				setTextList(ans);
			}
		}, [value]);


  const [text, setText] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [editText, setEditText] = useState("");

  const [optionsStr] = useRemoteField(`${question_id}.OPTIONS`);
  let options: string[] = [];

  if(optionsStr){
    try {
      options = JSON.parse(optionsStr);
    } catch {}
  }
  let hints: string[] = []
  if(hint_value){
    try{
      if(!hint_value.includes('eg.')){
        hints = JSON.parse(hint_value)
      }
    }
    catch{}
  }

  const handleAddText = () => {
	
	const cleaned = text?.trim();
	if (!cleaned) return;
	let safeList: string[] = [];
	
	if (Array.isArray(textLists)) {
		safeList = textLists;
	} else {
		try {
		const parsed = JSON.parse(textLists as any);
		safeList = Array.isArray(parsed) ? parsed : [];
		} catch {
		safeList = [];
		}
	}
	if (!safeList.includes(cleaned) && !options.includes(cleaned)) {
		const newList = [...safeList, cleaned];

		setTextList(newList);
		setValue(JSON.stringify(newList), locked === -1);
	}
    setText("");
  };

  const handleDeleteText = (textToDelete: string) => {
    const newList = textLists.filter((v) => v !== textToDelete);
    setTextList(newList);
    setValue(JSON.stringify(newList), locked === -1);
  };

  const handleSaveEdit = (index: any) => {
    let updatedList = [...textLists];
    updatedList[index] = editText;
    setValue(JSON.stringify(updatedList));
    setEditIndex(null);
    setEditText("");
  };

  const handleCopyText = (textToCopy: any) => {
    navigator.clipboard.writeText(textToCopy);
  };

  return (
		<>
			<Stack
				direction="column"
				spacing={1}
				sx={{
					display: "block",
					alignItems: "stretch",
					gap: 1,
				}}
			>
				{hints.length > 0 &&
					value === "" &&
					!hint_value.includes("eg.") &&
					hints.map((value, index) => {
						return (
							<Box
								sx={{ alignItems: "center", gap: 1, display: "flex" }}
								key={index}
							>
								<Chip
									key={`${index}-${value}`}
									label={
										<Typography
											sx={{
												whiteSpace: "normal",
												overflow: "hidden",
												flexGrow: 1,
												padding: "5px",
												maxWidth: "100%",
												wordBreak: "break-word",
												fontStyle: "italic",
												color: "rgb(128 0 128 / 25%)",
											}}
										>
											{value}
										</Typography>
									}
									sx={{
										height: "auto",
										flexGrow: 1,
										justifyContent: "space-between",
										backgroundColor: "#f5f5f5",
										border: "1px dashed rgb(128 0 128 / 25%)",
									}}
								/>
							</Box>
						);
					})}
			</Stack>
			<Box>
				<Box>
					{textLists.length > 0 &&
						textLists.map((value, index) => (
							<Box
								key={value}
								sx={{
									width: "100%",
									display: "flex",
									alignItems: "center",
									marginBottom: 1,
								}}
							>
								<Chip
									label={
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												width: "100%",
											}}
										>
											<Typography
												sx={{
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
													fontStyle: options.includes(value)
														? "italic"
														: "normal",
													color: options.includes(value) ? "gray" : "inherit",
												}}
											>
												{value}
											</Typography>
											<Tooltip title="Copy">
												<ContentCopyIcon
													fontSize="small"
													onClick={(e) => {
														e.stopPropagation();
														handleCopyText(value);
													}}
													sx={{ cursor: "pointer", ml: 1 }}
												/>
											</Tooltip>
										</Box>
									}
									onDelete={() => handleDeleteText(value)}
									deleteIcon={
										<Tooltip title="Delete">	
											<IconButton disabled={locked > 0}>
												<DeleteIcon
													fontSize="small"
													sx={{ color: locked>0 ? undefined : "rgb(211, 47, 47)" }}
												/>
											</IconButton>
										</Tooltip>
									}
									sx={{
										width: "100%",
										justifyContent: "space-between",
										backgroundColor: options.includes(value)
											? "#e0e0e0"
											: "default",
										border: options.includes(value)
											? "1px dashed gray"
											: "none",
									}}
								/>
							</Box>
						))}
				</Box>
			</Box>
			<Box
				sx={{ display: "flex", alignItems: "center", gap: 1, marginTop: "2%" }}
			>
				<TextField
					id={question_id}
					fullWidth
					variant={inline ? "standard" : "outlined"}
					value={text}
					disabled={locked > 0}
					sx={{
						flexGrow: 1,
						bgcolor: locked ? "#00000011" : "none",
					}}
					required={required}
					onChange={(e) => setText(e.target.value)}
                    onBlur={() => {
                        if (text.trim() !== "") handleAddText();
                    }}
					placeholder={
						hint_value.length > 0 && hint_value.includes("eg.")
							? hint_value
							: ""
					}
				/>
				<IconButton
					disabled={locked > 0}
					color="primary"
					onClick={handleAddText}
					size="small"
					sx={{ p: 0 }}
				>
					<AddCircleIcon />
				</IconButton>
			</Box>
		</>
	);
};